import { Alert, Box, Button, CircularProgress, Stack } from '@mui/material';
import { Controller, FormProvider, useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { AppName } from '../../../data-models/integrations-app.data-model';
import { ConnectionDetailsSection } from '../components/ConnectionDetailsSection';
import { IntegrationDetailHeader } from '../components/IntegrationDetailHeader';
import { LabeledTextField } from '../components/LabeledTextField';
import { SettingsCard } from '../components/SettingsCard';
import { integrationsStateLoadable } from '../state/IntegrationsState';
import { AppMap } from '../utils/uiAppData';
import { AffinityFormValues } from './affinityConfig.schema';
import { useAffinityConfigFormState } from './useAffinityConfigFormState';
import { useAtomValue } from 'jotai';

export function AffinityConfiguration() {
  const integrationsLoadable = useAtomValue(integrationsStateLoadable);
  const integrationsData = integrationsLoadable.state === 'hasData' ? integrationsLoadable.data : undefined;
  const affinityEntry = integrationsData?.find((app) => app.appName === AppName.Affinity);
  const isConnected = !!affinityEntry?.isConnected;
  const uiData = AppMap.get(AppName.Affinity);

  const description = isConnected
    ? `Manage your ${uiData?.displayName ?? 'Affinity'} integration. Changes apply to your entire organization immediately after saving.`
    : `Connect ${uiData?.displayName ?? 'Affinity'} to Foresight. Changes apply to your entire organization immediately after saving.`;

  if (integrationsLoadable.state === 'loading') {
    return (
      <Stack alignItems='center' justifyContent='center' minHeight='400px'>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack width='100%' height='100%' p='12px' gap='1rem' overflow='auto'>
      <IntegrationDetailHeader appName={AppName.Affinity} description={description} />
      {isConnected ? (
        <SettingsCard>
          <ConnectionDetailsSection isConnected />
        </SettingsCard>
      ) : (
        <AffinityConnectForm />
      )}
    </Stack>
  );
}

function AffinityConnectForm() {
  const navigate = useNavigate();
  const { successMessage, errorMessage, methods, onSave, isLoading } = useAffinityConfigFormState();

  const handleCancel = () => navigate('/integrations/apps');

  return (
    <FormProvider {...methods}>
      <SettingsCard title='Connect Affinity'>
        {successMessage && <Alert severity='success'>{successMessage}</Alert>}
        {errorMessage && <Alert severity='error'>{errorMessage}</Alert>}

        <Box sx={{ width: { xs: '100%', md: '50%' } }}>
          <ApiKeyField />
        </Box>

        <Stack direction='row' gap='0.75rem' alignItems='center'>
          <Button color='secondary' variant='contained' size='small' onClick={onSave} disabled={isLoading}>
            Connect
          </Button>
          <Button
            color='secondary'
            variant='outlined'
            size='small'
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
        </Stack>
      </SettingsCard>
    </FormProvider>
  );
}

function ApiKeyField() {
  const {
    control,
    formState: { errors },
  } = useFormContext<AffinityFormValues>();

  return (
    <Controller
      name='apiKey'
      control={control}
      render={({ field }) => (
        <LabeledTextField
          {...field}
          label='Affinity API key'
          required
          type='password'
          placeholder='Enter your Affinity API key'
          error={!!errors.apiKey}
          helperText={errors.apiKey?.message}
        />
      )}
    />
  );
}
